
import * as React from "react"
import { Clock } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface TimePickerDemoProps {
  value?: string;
  onChange: (time: string) => void;
}

export function TimePickerDemo({ value, onChange }: TimePickerDemoProps) {
  const minuteRef = React.useRef<HTMLInputElement>(null)
  const hourRef = React.useRef<HTMLInputElement>(null)
  const [hour, setHour] = React.useState<string>(value?.split(":")?.[0] || "")
  const [minute, setMinute] = React.useState<string>(value?.split(":")?.[1] || "")
  const [open, setOpen] = React.useState(false)

  // Update internal state when value changes
  React.useEffect(() => {
    if (value) {
      const [h, m] = value.split(":")
      setHour(h)
      setMinute(m)
    }
  }, [value])

  // Create the formatted time string and call the onChange handler
  const handleTimeChange = () => {
    const formattedHour = hour.padStart(2, "0")
    const formattedMinute = minute.padStart(2, "0")
    const timeString = `${formattedHour}:${formattedMinute}`
    onChange(timeString)
  }

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHour = e.target.value.replace(/[^\d]/g, "")
    if (newHour === "") {
      setHour("")
      return
    }
    
    const hourNum = parseInt(newHour, 10)
    if (hourNum >= 0 && hourNum <= 23) {
      setHour(hourNum.toString())
      if (newHour.length >= 2 && minuteRef.current) {
        minuteRef.current.focus()
      }
    }
  }

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMinute = e.target.value.replace(/[^\d]/g, "")
    if (newMinute === "") {
      setMinute("")
      return
    }
    
    const minuteNum = parseInt(newMinute, 10)
    if (minuteNum >= 0 && minuteNum <= 59) {
      setMinute(minuteNum.toString())
    }
  }

  const handleInputBlur = () => {
    handleTimeChange()
  }

  const handlePopoverOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) {
      handleTimeChange()
    }
  }

  return (
    <Popover open={open} onOpenChange={handlePopoverOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal bg-[#1e293b] text-white border-input hover:bg-[#2d3748] hover:text-white",
            !value && "text-muted-foreground"
          )}
        >
          <Clock className="mr-2 h-4 w-4" />
          {value ? value : "Select time"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4 bg-[#1e293b] text-white">
        <div className="flex items-center space-x-2">
          <div className="grid gap-1">
            <Label htmlFor="hours" className="text-xs text-white">
              Hours
            </Label>
            <Input
              id="hours"
              ref={hourRef}
              className="w-16 bg-[#1e293b] text-white border-input"
              value={hour}
              onChange={handleHourChange}
              onBlur={handleInputBlur}
              inputMode="numeric"
              placeholder="00"
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="minutes" className="text-xs text-white">
              Minutes
            </Label>
            <Input
              id="minutes"
              ref={minuteRef}
              className="w-16 bg-[#1e293b] text-white border-input"
              value={minute}
              onChange={handleMinuteChange}
              onBlur={handleInputBlur}
              inputMode="numeric"
              placeholder="00"
            />
          </div>
          <div className="grid gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-5 bg-[#1e293b] text-white border-input hover:bg-[#2d3748]"
              onClick={() => {
                setOpen(false)
                handleTimeChange()
              }}
            >
              Set
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
